import api from './api';

export interface CompanyContact {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  phone?: string;
  linkedInUrl?: string;
  isPrimary: boolean;
  status: string;
  lemlistStatus?: string;
  lemlistCampaignId?: string | null;
  /** Rollen beim Kunden (Empfaenger von Sicherheitsinformationen aus Core). */
  kontaktRollen?: KontaktRolle[];
  advisoryOptOut?: boolean;
}

export type KontaktRolle = 'sicherheit' | 'isb' | 'dsb' | 'gf' | 'technik' | 'abrechnung';

export interface CompanyNote {
  id: string;
  companyId: string;
  text: string;
  kind: string;
  createdAt: string;
  author?: { name: string } | null;
}

export interface CompanyDeal {
  id: string;
  title: string;
  value: number;
  currency: string;
  status: string;
  stage?: { id: string; name: string };
}

export interface Company {
  id: string;
  name: string;
  domain?: string;
  website?: string;
  industry?: string;
  country?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  region?: string;
  size?: string;
  phone?: string;
  linkedInUrl?: string;
  source?: string;
  status: string;
  notesText?: string;
  /** Bestandskunde: erhaelt Sicherheitsinformationen aus Core. */
  istKunde?: boolean;
  kundennummer?: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  lastContactedAt?: string;
  contacts?: CompanyContact[];
  deals?: CompanyDeal[];
  notes?: CompanyNote[];
  _count?: { contacts: number; deals: number; activities?: number };
}

export interface Suppression {
  id: string;
  email?: string;
  domain?: string;
  reason?: string;
  createdAt: string;
}

export interface BackfillReport {
  geprueft: number;
  firmenNeu: number;
  zugeordnet: number;
  ohneDomain: number;
  aktivitaeten?: { geprueft: number; zugeordnet: number; ohneFirma: number };
}

export type EintragArt = 'aktivitaet' | 'notiz' | 'vorgang' | 'kontakt';

export interface AktenEintrag {
  id: string;
  art: EintragArt;
  kanal?: string;
  titel: string;
  text?: string;
  autor?: string;
  quelle?: string;
  zeitpunkt: string;
  leadEmail?: string;
  dealId?: string;
  leadId?: string;
  lemlistCampaignId?: string;
  handled?: boolean;
}

export interface AktenKennzahlen {
  kontakte: number;
  antworten: number;
  interesse: number;
  absagen: number;
  abmeldungen: number;
  unzustellbar: number;
  vorgaengeOffen: number;
  vorgaengeGewonnen: number;
  wertOffen: number;
  wertGewonnen: number;
  letzteBeruehrung: { zeitpunkt: string; titel: string; kampagne?: string | null; kampagneId?: string | null } | null;
}

export interface Akte {
  firma: Company;
  kennzahlen: AktenKennzahlen;
  chronik: AktenEintrag[];
}

export type Angebot = 'grc' | 'pentest' | 'assessment' | 'keins';

/** Account-Profil (account-profile.service.ts). "intern" nie in Mails. */
export interface CompanyProfile {
  id: string;
  kurzprofil?: string | null;
  anlaesse?: { titel: string; datum?: string; quelle?: string; relevanz?: string }[] | null;
  technik?: string | null;
  hypothese?: string | null;
  oeffner?: { beobachtung: string; relevanz: string }[] | null;
  stellen?: { titel: string; quelle?: string }[] | null;
  wettbewerber?: string[] | null;
  groesse?: string | null;
  sitzLand?: string | null;
  branchenbezug?: string | null;
  angebot?: Angebot | null;
  angebotBegruendung?: string | null;
  einstieg?: string | null;
  intern?: { finanzen?: string | null; probleme?: string | null } | null;
  quellen?: string[] | null;
  modell?: string | null;
  createdAt: string;
}

const companyService = {
  async getProfil(id: string): Promise<CompanyProfile | null> {
    const response = await api.get(`/companies/${id}/profil`);
    return response.data?.profil ?? null;
  },

  /** Dauert 20 bis 90 Sekunden (Recherche plus Modell). */
  async erstelleProfil(id: string): Promise<{ profil: CompanyProfile; einstiegVerworfen: string | null }> {
    const response = await api.post(`/companies/${id}/profil`, {}, { timeout: 180000 });
    return response.data;
  },

  async list(filter?: { status?: string; suche?: string }): Promise<Company[]> {
    const response = await api.get('/companies', { params: filter || {} });
    return response.data;
  },

  async get(id: string): Promise<Company> {
    const response = await api.get(`/companies/${id}`);
    return response.data;
  },

  async getAkte(id: string, grenze?: number): Promise<Akte> {
    const response = await api.get(`/companies/${id}/akte`, {
      params: grenze ? { grenze } : {},
    });
    return response.data;
  },

  async create(daten: Partial<Company>): Promise<Company> {
    const response = await api.post('/companies', daten);
    return response.data;
  },

  async update(id: string, daten: Partial<Company>): Promise<Company> {
    const response = await api.patch(`/companies/${id}`, daten);
    return response.data;
  },

  async addNote(id: string, text: string, kind = 'note'): Promise<CompanyNote> {
    const response = await api.post(`/companies/${id}/notes`, { text, kind });
    return response.data;
  },

  async assignContact(id: string, leadId: string, isPrimary = false): Promise<CompanyContact> {
    const response = await api.post(`/companies/${id}/contacts`, { leadId, isPrimary });
    return response.data;
  },

  async addContact(id: string, daten: {
    email: string; firstName?: string; lastName?: string;
    jobTitle?: string; phone?: string; linkedInUrl?: string; isPrimary?: boolean;
  }): Promise<CompanyContact> {
    const response = await api.post(`/companies/${id}/contacts`, daten);
    return response.data;
  },

  async updateContact(leadId: string, daten: { kontaktRollen?: KontaktRolle[]; advisoryOptOut?: boolean }): Promise<CompanyContact> {
    const response = await api.patch(`/companies/contacts/${leadId}`, daten);
    return response.data;
  },

  async removeContact(leadId: string): Promise<void> {
    await api.delete(`/companies/contacts/${leadId}`);
  },

  async backfill(grenze?: number): Promise<BackfillReport> {
    const response = await api.post('/companies/backfill', { grenze });
    return response.data;
  },

  async getSuppressions(): Promise<Suppression[]> {
    const response = await api.get('/companies/suppressions');
    return response.data;
  },

  async addSuppression(daten: { email?: string; domain?: string; reason?: string }): Promise<Suppression> {
    const response = await api.post('/companies/suppressions', daten);
    return response.data;
  },

  async removeSuppression(id: string): Promise<void> {
    await api.delete(`/companies/suppressions/${id}`);
  },
};

export default companyService;
